import type { Meeting, MeetingFreq } from '@/api/types';

/**
 * Upcoming: not held yet. Past: the day has come or passed, or the studio has already written the outcome;
 * a meeting that took place without an outcome is past too, it must not stay under «Ближайшие».
 */
export function splitMeetings(meetings: Meeting[], now: number = Date.now()): { upcoming: Meeting[]; past: Meeting[] } {
  const isPast = (m: Meeting) => m.status === 'held' || !!m.outcome || new Date(m.at).getTime() < now;
  return {
    upcoming: meetings.filter((m) => !isPast(m)).sort((a, b) => a.at.localeCompare(b.at)),
    past: meetings.filter(isPast).sort((a, b) => b.at.localeCompare(a.at)),
  };
}

const freqLabels: Record<MeetingFreq, string> = { weekly: 'каждую неделю', biweekly: 'раз в две недели', monthly: 'раз в месяц' };
/** «каждую неделю» for a meeting of a series; empty for a single one. */
export const recurrenceLabel = (m: Meeting) => (m.recurrence ? freqLabels[m.recurrence.freq] : '');

export const isCanceled = (m: Meeting) => m.status === 'canceled';
export const meetingEnd = (m: Meeting) => new Date(new Date(m.at).getTime() + (m.durationMin ?? 60) * 60_000);
/** «Прошла»: not canceled, and the outcome is in or its end has passed; the same rule as the BFF's «held». */
export const isHeld = (m: Meeting, now: number = Date.now()) => !isCanceled(m) && (m.status === 'held' || !!m.outcome || meetingEnd(m).getTime() <= now);

const stamp = (d: Date) => d.toISOString().replace(/[-:]|\.\d{3}/g, '');

/** «Добавить в Google Календарь»: a template link. Only the main contour offers it, the RU one never links to Google. */
export function googleCalendarUrl(m: Meeting): string {
  const details = [m.agenda.length ? `Повестка:\n${m.agenda.map((a) => `- ${a}`).join('\n')}` : '', m.link ? `Ссылка: ${m.link}` : ''].filter(Boolean).join('\n\n');
  const q = (v: string) => encodeURIComponent(v);
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${q(m.title)}&dates=${stamp(new Date(m.at))}/${stamp(meetingEnd(m))}&details=${q(details)}&location=${q(m.link ?? '')}`;
}

/** A file name for a downloaded .ics: letters and digits of the title. */
export const icsName = (m: Meeting, series = false) => `${(m.title.replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-|-$/g, '') || 'meeting').slice(0, 40)}${series ? '-series' : ''}.ics`;
